import { CashoutBreakdown } from '../../components/CashoutBreakdown';
import { HeldCashoutsPanel } from '../../components/HeldCashoutsPanel';
import { useEffect, useState, useCallback, useRef } from 'react';
import { DollarSign, MessageSquare, Clock, CheckCircle2, AlertTriangle, Loader2, Bell, BellOff, RefreshCcw, History, Download, Copy } from 'lucide-react';
import { PlayerName, usePlayerDrilldown } from '../../components/PlayerDrilldownContext';
import { useStaffAlerts } from '../../contexts/StaffAlertsContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { setTabSignal, playChime } from '../../lib/tabAlerts';
import { useShowMore } from '../../components/useShowMore';
import PlayItBackDialog from '../../components/PlayItBackDialog';
import PlayBackStatsBadge from '../../components/PlayBackStatsBadge';
import PartialPaidDialog from '../../components/PartialPaidDialog';
import MessageCustomerButton from '../../components/admin/MessageCustomerButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MUTE_KEY = 'admin.pendingPayouts.muted';
const QUICK_PRESETS = [5, 10, 20, 50];

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    } catch {
        return iso;
    }
};

/**
 * SLA urgency band — based on how much of the expected_max_hours window has elapsed.
 *  green   < 50%   plenty of time
 *  yellow  50-80%  getting close
 *  orange  80-100% warning, almost overdue
 *  red     > 100%  overdue
 */
const getUrgency = (hoursWaiting, maxHours) => {
    const max = maxHours || 24;
    const pct = hoursWaiting != null ? (hoursWaiting / max) * 100 : 0;
    if (pct >= 100) return { level: 'overdue', color: '#FF3B30', label: 'OVERDUE',  pct };
    if (pct >= 80)  return { level: 'critical', color: '#FF8C00', label: 'CRITICAL', pct };
    if (pct >= 50)  return { level: 'soon',    color: '#FFD700', label: 'SOON',     pct };
    return                { level: 'ok',      color: '#39FF14', label: 'OK',       pct };
};

// What the customer still needs to be SENT. Payments are recorded in NET dollars
// (remaining_to_pay = net still owed); unpaid_gross = credits a play-back may return.
const computeRemaining = (r) => {
    const amt = Math.abs(r.amount || 0);
    const fee = Number(r.fee_amount || 0);
    const net = Number(r.net_amount || 0);
    const original = Number(r.original_amount || amt) || amt;
    const remainingNet = (r.remaining_to_pay != null) ? Number(r.remaining_to_pay) : (net > 0 ? net : amt);
    const remainingGross = (r.unpaid_gross != null) ? Number(r.unpaid_gross) : amt;
    const remainingFee = Math.max(0, remainingGross - remainingNet);
    return {
        amt, fee, net, original, remainingGross, remainingNet, remainingFee,
        hasFee: fee > 0 && net > 0 && Math.abs(net - amt) > 0.01,
        isPartial: (r.paid_total || 0) > 0.01,
    };
};


const PendingPayoutsPanel = ({ onChanged } = {}) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [quickBusyKey, setQuickBusyKey] = useState(null);
    const [dialogRow, setDialogRow] = useState(null);
    // Full-screen QR preview overlay — for cashier scanning on-phone.
    const [qrPreview, setQrPreview] = useState(null);
    const [dialogBusy, setDialogBusy] = useState(false);
    const [playBackStats, setPlayBackStats] = useState({});
    const [muted, setMuted] = useState(() => {
        try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
    });
    // Track previous urgency level per row id to detect upward transitions (chime trigger)
    const prevUrgencyRef = useRef(new Map());
    const isFirstLoadRef = useRef(true);
    // "Surface missed" reconciliation tool — counts cashier-paid cashouts from
    // the last 7 days that are stuck with paid=true and never made it into
    // the admin queue (legacy data + pre-redeploy transactions).
    const [missedPreview, setMissedPreview] = useState({ missed_count: 0, missed_total: 0 });
    const [surfacing, setSurfacing] = useState(false);
    // Row currently open in the partial-paid dialog. Set by clicking Mark Paid.
    const [payingRow, setPayingRow] = useState(null);

    const toggleMute = () => {
        setMuted((m) => {
            const next = !m;
            try { localStorage.setItem(MUTE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
            return next;
        });
    };

    const fetchMissedPreview = useCallback(async () => {
        try {
            const { data } = await axios.get(
                `${API}/admin/cashouts/surface-missed-preview?days=7`,
                { withCredentials: true }
            );
            setMissedPreview(data || { missed_count: 0, missed_total: 0 });
        } catch { /* silent */ }
    }, []);

    const surfaceMissed = async () => {
        if (surfacing) return;
        if (missedPreview.missed_count === 0) {
            toast.info('No missed cashouts to surface — your queue is in sync.');
            return;
        }
        const sure = window.confirm(
            `Re-surface ${missedPreview.missed_count} cashier-paid cashout(s) ` +
            `totaling $${missedPreview.missed_total.toFixed(2)} into your Pending Payouts queue?\n\n` +
            `Each row will get a "Paid at counter" badge and the original payment time is preserved as audit. ` +
            `You can then click Mark Paid to acknowledge.`
        );
        if (!sure) return;
        setSurfacing(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/cashouts/surface-missed?days=7`,
                {},
                { withCredentials: true }
            );
            toast.success(
                data.surfaced === 0
                    ? 'No new cashouts found to surface.'
                    : `Surfaced ${data.surfaced} cashout${data.surfaced === 1 ? '' : 's'} into your queue.`
            );
            await Promise.all([fetchPending(), fetchMissedPreview()]);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to surface missed cashouts');
        } finally { setSurfacing(false); }
    };

    const fetchPending = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/pending-payouts`, { withCredentials: true });
            setRows(data || []);
        } catch (e) {
            if (!silent) toast.error(e.response?.data?.detail || 'Failed to load pending payouts');
        }
        if (!silent) setLoading(false);
    }, []);

    useEffect(() => {
        fetchPending();
        fetchMissedPreview();
        // Near-instant refresh so a self-serve play-back the customer just did
        // shows up within a couple seconds (admin-initiated actions already
        // refetch immediately). Poll the payouts list fast (3s); the heavier
        // "missed" reconciliation query stays on a slow 30s cadence.
        const fast = setInterval(() => { fetchPending(true); }, 3_000);
        const slow = setInterval(() => { fetchMissedPreview(); }, 30_000);
        return () => { clearInterval(fast); clearInterval(slow); };
    }, [fetchPending, fetchMissedPreview]);

    // Fetch per-player play-back stats whenever rows change (responsible-play badge)
    useEffect(() => {
        const ids = [...new Set(rows.map((r) => r.user_id).filter(Boolean))];
        if (ids.length === 0) { setPlayBackStats({}); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.post(
                    `${API}/admin/play-back-stats`,
                    { user_ids: ids },
                    { withCredentials: true }
                );
                if (!cancelled) setPlayBackStats(data?.stats || {});
            } catch { /* non-critical */ }
        })();
        return () => { cancelled = true; };
    }, [rows]);

    // Detect transitions into critical/overdue + drive favicon/title alerts
    const { openPlayer } = usePlayerDrilldown();
    const { push: pushAlert } = useStaffAlerts();
    useEffect(() => {
        const next = new Map();
        let overdueCount = 0;
        let criticalCount = 0;
        const escalatedRows = [];

        rows.forEach((r) => {
            const u = getUrgency(r.hours_since_approved, r.expected_max_hours);
            next.set(r.id, u.level);
            if (u.level === 'overdue') overdueCount += 1;
            if (u.level === 'critical') criticalCount += 1;
            if (!isFirstLoadRef.current) {
                const prev = prevUrgencyRef.current.get(r.id);
                // Only chime on UPWARD transitions into critical/overdue (skip first load)
                const escalated =
                    (u.level === 'critical' && prev !== 'critical' && prev !== 'overdue') ||
                    (u.level === 'overdue' && prev !== 'overdue');
                if (escalated) escalatedRows.push({ row: r, level: u.level });
            }
        });

        prevUrgencyRef.current = next;

        // Push to centralized tab-alert manager (priority: overdue > critical)
        if (overdueCount > 0) {
            setTabSignal('pending-payouts', 'overdue', overdueCount + criticalCount);
        } else if (criticalCount > 0) {
            setTabSignal('pending-payouts', 'critical', criticalCount);
        } else {
            setTabSignal('pending-payouts', null);
        }

        if (escalatedRows.length > 0 && !muted) {
            playChime();
            // Toast each escalated cashout with a clickable `View` action that
            // opens the player's drilldown — admin can verify balance / loan
            // status in 1 click before approving. Cap at 3 toasts per tick to
            // avoid spam if a queue of cashouts crosses the threshold at once.
            escalatedRows.slice(0, 3).forEach(({ row, level }) => {
                const userName = row.user_name || row.player_name || 'a player';
                const amt = Math.abs(Number(row.net_amount ?? row.amount ?? 0)).toFixed(2);
                const icon = level === 'overdue' ? '🚨' : '⚡';
                const verb = level === 'overdue' ? 'OVERDUE' : 'critical';
                const title = `${icon} Cashout ${verb} — ${userName} · $${amt}`;
                const fn = level === 'overdue' ? toast.error : toast.warning;
                fn(title, {
                    duration: 12000,
                    action: row.user_id ? {
                        label: 'View',
                        onClick: () => openPlayer(row.user_id),
                    } : undefined,
                });
                pushAlert({
                    id: `payout-${row.id || row.transaction_id}-${level}`,
                    level: level === 'overdue' ? 'error' : 'warning',
                    icon,
                    title: `Cashout ${verb} — ${userName} · $${amt}`,
                    description: 'Tap View to verify before approving.',
                    userId: row.user_id,
                });
            });
            if (escalatedRows.length > 3) {
                toast.warning(`+${escalatedRows.length - 3} more cashouts escalated — see queue`);
            }
        }
        isFirstLoadRef.current = false;
    }, [rows, muted, openPlayer, pushAlert]);

    // Clear our signal contribution on unmount
    useEffect(() => {
        return () => setTabSignal('pending-payouts', null);
    }, []);

    const markPaid = async (transaction_id, userName, isAmbassador = false) => {
        if (!window.confirm(
            isAmbassador
                ? `Mark this AMBASSADOR PAY DAY payout to ${userName} as PAID? They will be deducted from pending balance.`
                : `Mark this cashout to ${userName} as PAID?`
        )) return;
        setBusyId(transaction_id);
        try {
            if (isAmbassador) {
                await axios.post(`${API}/admin/ambassador/pay-day/action`,
                    { transaction_id, action: 'paid' }, { withCredentials: true });
            } else {
                await axios.post(`${API}/admin/cashout-mark-paid`,
                    { transaction_id }, { withCredentials: true });
            }
            toast.success(`Marked paid — ${userName}`);
            await fetchPending();
            onChanged?.();
        } catch (e) {
            const msg = e.response?.data?.detail || 'Failed to mark paid';
            const playedBack = e.response?.status === 409 || /played back/i.test(msg);
            toast.error(msg, { duration: playedBack ? 9000 : 4000 });
            // A 409 means the state changed under us (e.g. the customer just
            // played it back). Refresh so the stale row drops out of the queue.
            if (playedBack) { await fetchPending(); onChanged?.(); }
        }
        setBusyId(null);
    };

    const playItBack = (row) => setDialogRow(row);
    const closeDialog = (open) => { if (!open) setDialogRow(null); };

    const confirmPlayBack = async ({ amount, note }) => {
        if (!dialogRow) return;
        setDialogBusy(true);
        try {
            const body = { transaction_id: dialogRow.id };
            if (amount != null) body.amount = amount;
            if (note) body.note = note;
            const { data } = await axios.post(
                `${API}/admin/cashout-play-back`,
                body,
                { withCredentials: true }
            );
            toast.success(
                `Played back $${data.played_back_now.toFixed(2)} to ${dialogRow.user_name}'s balance` +
                (data.already_paid_cash > 0 ? ` · $${data.already_paid_cash.toFixed(2)} already paid in cash` : '') +
                ` · $${data.remaining_owed.toFixed(2)} unpaid credits left · new balance $${data.new_balance.toFixed(2)}` +
                (data.bonus_granted ? ` · +$${data.bonus_granted.toFixed(2)} load bonus` : ''),
                { duration: 8000 }
            );
            setDialogRow(null);
            await fetchPending();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to play back');
        }
        setDialogBusy(false);
    };

    const fireQuickPlayBack = async (row, amount) => {
        const key = `${row.id}-${amount ?? 'all'}`;
        setQuickBusyKey(key);
        try {
            const body = { transaction_id: row.id };
            if (amount != null) body.amount = amount;
            const { data } = await axios.post(
                `${API}/admin/cashout-play-back`,
                body,
                { withCredentials: true }
            );
            toast.success(
                `Played back $${data.played_back_now.toFixed(2)} to ${row.user_name}'s balance` +
                (data.already_paid_cash > 0 ? ` · $${data.already_paid_cash.toFixed(2)} already paid in cash` : '') +
                ` · $${data.remaining_owed.toFixed(2)} unpaid credits left · new balance $${data.new_balance.toFixed(2)}`,
                { duration: 8000 }
            );
            await fetchPending();
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to play back');
        }
        setQuickBusyKey(null);
    };

    const totalOwed = rows.reduce((s, r) => s + (r.remaining_to_pay != null ? r.remaining_to_pay : Math.abs(r.amount || 0)), 0);
    const { visibleItems: visibleRows, ShowMoreButton, total: rowTotal, limit: rowLimit } = useShowMore(rows, 10, 10);
    // Bucket counts by urgency
    const buckets = rows.reduce((acc, r) => {
        const u = getUrgency(r.hours_since_approved, r.expected_max_hours);
        acc[u.level] = (acc[u.level] || 0) + 1;
        return acc;
    }, {});
    const overdueCount = buckets.overdue || 0;
    const criticalCount = buckets.critical || 0;
    const soonCount = buckets.soon || 0;

    return (
        <div className="card-dark p-6 mb-4" data-testid="pending-payouts-panel">
            <HeldCashoutsPanel onReleased={() => fetchPending()} />
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-xl bg-[#FFD700]/15 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                        <h3 className="text-base font-bold text-white">Pending Payouts</h3>
                        <p className="text-[11px] text-[#A0AAB2]">
                            Approved cashouts awaiting payment
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {overdueCount > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" />
                            {overdueCount} overdue
                        </span>
                    )}
                    {criticalCount > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF8C00]/15 text-[#FF8C00] border border-[#FF8C00]/40">
                            {criticalCount} critical
                        </span>
                    )}
                    {soonCount > 0 && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40">
                            {soonCount} soon
                        </span>
                    )}
                    <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40">
                        OWED ${totalOwed.toFixed(2)}
                    </span>
                    <button
                        type="button"
                        onClick={surfaceMissed}
                        disabled={surfacing || missedPreview.missed_count === 0}
                        title={
                            missedPreview.missed_count === 0
                                ? 'No missed cashier-paid cashouts in the last 7 days.'
                                : `Re-surface ${missedPreview.missed_count} cashier-paid cashout(s) worth $${missedPreview.missed_total.toFixed(2)} into this queue.`
                        }
                        data-testid="pending-payouts-surface-missed"
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider transition border ${
                            missedPreview.missed_count > 0
                                ? 'bg-orange-500/15 text-orange-300 border-orange-500/40 hover:bg-orange-500/25'
                                : 'bg-white/5 text-zinc-600 border-white/10 cursor-not-allowed'
                        }`}
                    >
                        {surfacing ? <Loader2 className="w-3 h-3 animate-spin" /> : <History className="w-3 h-3" />}
                        Surface Missed
                        {missedPreview.missed_count > 0 && (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-500/30 text-orange-100 text-[9px]">
                                {missedPreview.missed_count}
                            </span>
                        )}
                    </button>
                    <button
                        type="button"
                        onClick={toggleMute}
                        title={muted ? 'Unmute alerts' : 'Mute alerts'}
                        data-testid="pending-payouts-mute"
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            muted
                                ? 'bg-white/5 text-[#666] hover:text-white'
                                : 'bg-[#39FF14]/15 text-[#39FF14] hover:bg-[#39FF14]/25'
                        }`}
                    >
                        {muted ? <BellOff className="w-3.5 h-3.5" /> : <Bell className="w-3.5 h-3.5" />}
                    </button>
                </div>
            </div>

            {/* SLA legend */}
            {rows.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 mb-3 pb-2 border-b border-white/5">
                    <span className="text-[10px] text-[#A0AAB2] uppercase tracking-wider font-bold">SLA</span>
                    {[
                        { color: '#39FF14', label: 'OK',       range: '<50%' },
                        { color: '#FFD700', label: 'SOON',     range: '50-80%' },
                        { color: '#FF8C00', label: 'CRITICAL', range: '80-100%' },
                        { color: '#FF3B30', label: 'OVERDUE',  range: '>100%' },
                    ].map((b) => (
                        <span key={b.label} className="inline-flex items-center gap-1.5 text-[10px]">
                            <span className="inline-block w-2 h-2 rounded-full" style={{ background: b.color, boxShadow: `0 0 6px ${b.color}55` }} />
                            <span className="font-bold" style={{ color: b.color }}>{b.label}</span>
                            <span className="text-[#666]">{b.range}</span>
                        </span>
                    ))}
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="w-6 h-6 text-[#FFD700] animate-spin" />
                </div>
            ) : rows.length === 0 ? (
                <div className="text-center py-10">
                    <CheckCircle2 className="w-12 h-12 text-[#39FF14] mx-auto mb-2" />
                    <p className="text-[#A0AAB2] text-sm">All caught up — no pending payouts.</p>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-white/10">
                                <TableHead className="text-[#A0AAB2]">Player</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                <TableHead className="text-[#A0AAB2]">Method</TableHead>
                                <TableHead className="text-[#A0AAB2]">Send To</TableHead>
                                <TableHead className="text-[#A0AAB2]">Approved</TableHead>
                                <TableHead className="text-[#A0AAB2]">Waiting</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleRows.map((r) => {
                                const amt = Math.abs(r.amount || 0);
                                const urgency = getUrgency(r.hours_since_approved, r.expected_max_hours);
                                const overdue = urgency.level === 'overdue';
                                return (
                                    <TableRow
                                        key={r.id}
                                        className={`border-white/10 ${overdue ? 'bg-[#FF3B30]/5' : ''} relative ${r.payout_qr_data_url ? 'cursor-pointer hover:bg-white/[0.03]' : ''}`}
                                        data-testid={`pending-payout-row-${r.id}`}
                                        style={{
                                            // left-edge color tape signaling SLA urgency
                                            boxShadow: `inset 4px 0 0 0 ${urgency.color}`,
                                        }}
                                        onClick={(e) => {
                                            // Auto-open QR overlay when the admin taps
                                            // anywhere on a row that has an uploaded QR.
                                            // The QR is what cashiers scan to send the
                                            // money, so surfacing it on a single tap
                                            // (instead of hunting for the thumbnail) makes
                                            // paying out faster. We ignore clicks that
                                            // originated in an interactive element so we
                                            // don't fight with buttons/inputs/links.
                                            if (!r.payout_qr_data_url) return;
                                            const t = e.target;
                                            if (t.closest('button, a, input, textarea, select, [role="button"], [role="dialog"]')) return;
                                            setQrPreview(r.payout_qr_data_url);
                                        }}
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <PlayerName id={r.user_id} className="text-white text-sm font-medium">
                                                    {r.user_name}
                                                </PlayerName>
                                                {r.is_ambassador && (
                                                    <span
                                                        className="text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/50 uppercase tracking-wider"
                                                        title="Ambassador PAY DAY payout — not from player wallet"
                                                        data-testid={`pay-day-badge-${r.id}`}
                                                    >
                                                        PAY DAY
                                                    </span>
                                                )}
                                                <PlayBackStatsBadge stats={playBackStats[r.user_id]} userId={r.user_id} />
                                                {(r.paid_total > 0 || Number(r.partial_play_back_total) > 0) && (
                                                <CashoutBreakdown b={r.breakdown} testId={`pending-payout-breakdown-${r.id}`} />
                                            )}
                                            {Number(r.partial_play_back_total) > 0 && (
                                                    <span
                                                        className="text-[9px] font-black px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-200 border border-violet-400/60 uppercase tracking-wide inline-flex items-center gap-1"
                                                        title={`$${Number(r.partial_play_back_total).toFixed(2)} of this cashout was already played back — the amount below is the reduced remainder. Verify before sending.`}
                                                        data-testid={`partial-playback-badge-${r.id}`}
                                                    >
                                                        <AlertTriangle className="w-2.5 h-2.5" />
                                                        Played back ${Number(r.partial_play_back_total).toFixed(2)} — verify amount
                                                    </span>
                                                )}
                                                {r.cashier_approved && (
                                                    <span
                                                        className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/40 uppercase tracking-wide"
                                                        title={`Cash already handed over at the counter by ${r.cashier_name || r.processed_by_name || 'cashier'}`}
                                                        data-testid={`cashier-approved-badge-${r.id}`}
                                                    >
                                                        Paid at counter · {r.cashier_name || r.processed_by_name || 'cashier'}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[#A0AAB2] text-[11px]">{r.user_email}</div>
                                            {r.payout_qr_data_url && (
                                                <div className="text-[9px] font-bold text-[#39FF14] uppercase tracking-wider mt-0.5 flex items-center gap-1">
                                                    <span>▸</span> Tap row to open QR
                                                </div>
                                            )}
                                            {r.user_phone && (
                                                <a href={`sms:${r.user_phone.replace(/[^0-9+]/g, '')}`}
                                                   className="text-[10px] text-[#FFD700] inline-flex items-center gap-1 mt-0.5 hover:underline">
                                                    <MessageSquare className="w-3 h-3" /> {r.user_phone}
                                                </a>
                                            )}
                                            <div className="mt-1">
                                                <MessageCustomerButton userId={r.user_id} userName={r.user_name} label="Message customer" />
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            {/* SEND THIS AMOUNT — Feb 2026
                                                Show NET (after 6% fee) in
                                                big bold green so the cashier
                                                never accidentally sends the
                                                gross. Fee + gross shown as
                                                small reference under it. */}
                                            {(() => {
                                                const rem = computeRemaining(r);
                                                if (rem.hasFee) {
                                                    return (
                                                        <div className="flex flex-col items-end gap-0.5" data-testid={`pending-payout-amount-${r.id}`}>
                                                            <div className="text-[9px] uppercase tracking-widest text-[#39FF14] font-black">
                                                                {rem.isPartial ? 'Send remaining' : 'Send this'}
                                                            </div>
                                                            <div
                                                                className="font-mono font-black text-[#39FF14] text-lg leading-none"
                                                                data-testid={`pending-payout-net-${r.id}`}
                                                            >
                                                                ${rem.remainingNet.toFixed(2)}
                                                            </div>
                                                            <div className="text-[10px] text-[#A0AAB2] font-mono">
                                                                {rem.isPartial
                                                                    ? <>remaining gross ${rem.remainingGross.toFixed(2)} · fee ${rem.remainingFee.toFixed(2)}</>
                                                                    : <>gross ${rem.amt.toFixed(2)} · fee ${rem.fee.toFixed(2)}</>}
                                                            </div>
                                                            {rem.isPartial && (
                                                                <div className="text-[9px] text-[#FFD700] font-mono">
                                                                    ${(r.paid_total || 0).toFixed(2)} already sent of ${Number(r.owed_net ?? rem.net).toFixed(2)} net
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }
                                                // Fallback for ambassador PAY DAY / legacy rows with no fee.
                                                return (
                                                    <div className="font-mono font-black text-[#FFD700]" data-testid={`pending-payout-amount-${r.id}`}>
                                                        ${rem.remainingGross.toFixed(2)}
                                                    </div>
                                                );
                                            })()}
                                            {Number(r.partial_play_back_total) > 0 && (
                                                <div className="text-[10px] text-[#A0AAB2] font-mono mt-1 leading-tight"
                                                     data-testid={`playback-history-${r.id}`}
                                                     title="Original cashout · amount the customer played back · what's still owed">
                                                    <span className="text-white">${Number(r.original_amount || 0).toFixed(2)}</span> cashout
                                                    <span className="text-[#00E0FF]"> · ${Number(r.partial_play_back_total).toFixed(2)} played back</span>
                                                    <span className="text-[#FFD700]"> · ${Number(r.remaining_to_pay != null ? r.remaining_to_pay : Math.abs(r.amount || 0)).toFixed(2)} owed</span>
                                                </div>
                                            )}
                                            {r.large_payout && (
                                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FF007F]/15 text-[#FF007F] border border-[#FF007F]/40">
                                                    LARGE · 48hr
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {r.payout_method ? (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                                                    {r.payout_method}
                                                </span>
                                            ) : (
                                                <span className="text-[#666] text-xs italic">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {r.payout_handle ? (
                                                <div className="flex flex-col gap-0.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => { navigator.clipboard?.writeText(r.payout_handle); toast.success('Handle copied'); }}
                                                        className="font-mono text-xs text-white hover:text-[#FFD700] transition-colors text-left"
                                                        title="Click to copy"
                                                    >
                                                        {r.payout_handle}
                                                    </button>
                                                    {/* TapSNDR ticket fields — click any line to copy for paste
                                                        into the redemption page. Only render if we have them. */}
                                                    {r.payout_name_on_account && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { navigator.clipboard?.writeText(r.payout_name_on_account); toast.success('Name copied'); }}
                                                            className="text-[10px] text-[#A0AAB2] hover:text-[#FFD700] transition-colors text-left"
                                                            title="Click to copy — name on account"
                                                            data-testid="pending-name-on-account"
                                                        >
                                                            👤 {r.payout_name_on_account}
                                                        </button>
                                                    )}
                                                    {r.payout_email && (
                                                        <button
                                                            type="button"
                                                            onClick={() => { navigator.clipboard?.writeText(r.payout_email); toast.success('Email copied'); }}
                                                            className="text-[10px] text-[#A0AAB2] hover:text-[#FFD700] transition-colors text-left truncate max-w-[180px]"
                                                            title="Click to copy — email"
                                                            data-testid="pending-payout-email"
                                                        >
                                                            ✉️ {r.payout_email}
                                                        </button>
                                                    )}
                                                    {r.payout_qr_data_url && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setQrPreview(r.payout_qr_data_url)}
                                                            className="mt-1 inline-flex items-center gap-1.5 group"
                                                            title="Tap to open QR full-screen · scan · download"
                                                            data-testid={`pending-payout-qr-btn-${r.id}`}
                                                        >
                                                            <div className="relative">
                                                                <img
                                                                    src={r.payout_qr_data_url}
                                                                    alt="Player payment QR"
                                                                    className="w-11 h-11 rounded border-2 border-[#FFD700]/70 object-cover group-hover:border-[#39FF14] transition"
                                                                    data-testid="pending-payout-qr"
                                                                />
                                                                <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#39FF14] border border-black flex items-center justify-center text-black font-black text-[8px]">↗</span>
                                                            </div>
                                                            <span className="text-[9px] font-bold text-[#39FF14] uppercase tracking-wider leading-tight">
                                                                Open<br />QR
                                                            </span>
                                                        </button>
                                                    )}
                                                </div>
                                            ) : (
                                                <span className="text-[#666] text-xs italic">in-person</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-[#A0AAB2] text-xs">
                                            {formatDate(r.processed_at)}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                <span
                                                    className="text-xs font-bold inline-flex items-center gap-1"
                                                    style={{ color: urgency.color }}
                                                >
                                                    <Clock className="w-3 h-3" />
                                                    {r.hours_since_approved != null ? `${r.hours_since_approved}h` : '—'}
                                                    <span className="text-[10px] opacity-70">/ {r.expected_max_hours || 24}h</span>
                                                </span>
                                                {/* SLA progress bar */}
                                                <div className="h-1 w-20 rounded-full bg-white/10 overflow-hidden">
                                                    <div
                                                        className="h-full rounded-full transition-all"
                                                        style={{
                                                            width: `${Math.min(100, urgency.pct)}%`,
                                                            background: urgency.color,
                                                            boxShadow: overdue ? `0 0 6px ${urgency.color}` : 'none',
                                                        }}
                                                    />
                                                </div>
                                                <span
                                                    className="text-[9px] font-black tracking-wider"
                                                    style={{ color: urgency.color }}
                                                >
                                                    {urgency.label}
                                                </span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2 flex-wrap">
                                                <Button
                                                    size="sm"
                                                    onClick={() => {
                                                        // Open ONLY the Mark-Paid dialog. The payout QR
                                                        // (if any) is rendered inside that dialog now —
                                                        // we must NOT also open the separate full-screen
                                                        // QR overlay here, because a Radix modal sets
                                                        // pointer-events:none on <body>, which made that
                                                        // overlay impossible to close and trapped the
                                                        // admin so they couldn't mark the payout paid.
                                                        setPayingRow(r);
                                                    }}
                                                    disabled={busyId === r.id}
                                                    className={`font-bold ${r.is_partial_paid
                                                        ? 'bg-amber-400 text-black hover:bg-amber-300'
                                                        : 'bg-[#39FF14] text-black hover:bg-[#39FF14]/80'}`}
                                                    data-testid={`mark-paid-btn-${r.id}`}
                                                    title={r.is_partial_paid
                                                        ? `Partial: paid $${(r.paid_total ?? 0).toFixed(2)} of $${(r.owed_net ?? r.original_amount ?? 0).toFixed(2)} net — click to add or edit`
                                                        : undefined}
                                                >
                                                    {busyId === r.id ? (
                                                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                    ) : (
                                                        <>
                                                            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                                            {r.is_partial_paid ? `${(r.paid_total ?? 0).toFixed(0)}/${(r.owed_net ?? r.original_amount ?? 0).toFixed(0)} paid` : 'Mark Paid'}
                                                        </>
                                                    )}
                                                </Button>
                                                {!r.is_ambassador && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => playItBack(r)}
                                                        disabled={busyId === r.id}
                                                        className="border-[#00E0FF]/40 text-[#00E0FF] hover:bg-[#00E0FF]/10"
                                                        data-testid={`play-back-btn-${r.id}`}
                                                        title="Refund credits to player — supports partial amount"
                                                    >
                                                        <RefreshCcw className="w-3.5 h-3.5 mr-1" />
                                                        Play It Back
                                                    </Button>
                                                )}
                                                {/* Copy-for-TapSNDR — packs every field the redemption
                                                    page asks for into the clipboard in one tap so the
                                                    cashier can paste straight in. */}
                                                {r.payout_handle && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => {
                                                            const rem = computeRemaining(r);
                                                            const method = r.payout_method || '';
                                                            // Copy the NET STILL OWED (after 6% fee, minus
                                                            // any partial payments) so a rushed cashier
                                                            // can't paste the full gross or the full net.
                                                            const sendAmt = rem.hasFee ? rem.remainingNet.toFixed(2) : rem.remainingGross.toFixed(2);
                                                            const grossRef = rem.remainingGross.toFixed(2);
                                                            const isZelle = method === 'Zelle';
                                                            const block = isZelle
                                                                ? `Send $${sendAmt} via Zelle (net after 6% fee)\n` +
                                                                  `Recipient: ${r.payout_handle || ''}\n` +
                                                                  `Name: ${r.payout_name_on_account || ''}\n` +
                                                                  `Memo/Email on file: ${r.payout_email || ''}`
                                                                : `Amount: $${sendAmt}  (net after 6% fee — gross owed $${grossRef})\n` +
                                                                  `Method: ${method}\n` +
                                                                  `Handle: ${r.payout_handle || ''}\n` +
                                                                  `Name on Account: ${r.payout_name_on_account || ''}\n` +
                                                                  `Email: ${r.payout_email || ''}`;
                                                            navigator.clipboard?.writeText(block);
                                                            toast.success(isZelle ? `Zelle details copied · send $${sendAmt}` : `Details copied · send $${sendAmt} (net)`);
                                                        }}
                                                        className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10"
                                                        data-testid={`copy-tapsndr-btn-${r.id}`}
                                                        title="Copy amount + method + handle + name + email in one block"
                                                    >
                                                        📋 Copy for TapSNDR
                                                    </Button>
                                                )}
                                            </div>
                                            {!r.is_ambassador && (() => {
                                                const paid = r.paid_total || 0;
                                                // unpaid remainder = what can actually be played back
                                                const remaining = (r.unpaid_gross != null)
                                                    ? r.unpaid_gross
                                                    : Math.max(0, Math.abs(r.amount || 0) - paid);
                                                const presets = QUICK_PRESETS.filter((p) => p < remaining);
                                                if (remaining <= 0) return null;
                                                return (
                                                    <div className="flex items-center justify-end gap-1.5 mt-2 flex-wrap" data-testid={`quick-playback-row-${r.id}`}>
                                                        <span className="text-[9px] uppercase tracking-wider text-[#A0AAB2] font-bold" data-testid={`playback-owed-hint-${r.id}`}>
                                                            {paid > 0
                                                                ? `Unpaid $${remaining.toFixed(2)} credits · $${paid.toFixed(2)} already sent · play-back:`
                                                                : 'Quick play-back:'}
                                                        </span>
                                                        {presets.map((p) => {
                                                            const key = `${r.id}-${p}`;
                                                            return (
                                                                <button
                                                                    type="button"
                                                                    key={p}
                                                                    onClick={() => fireQuickPlayBack(r, p)}
                                                                    disabled={quickBusyKey === key}
                                                                    className="px-2.5 py-1 rounded-full text-[11px] font-black bg-[#00E0FF]/10 text-[#00E0FF] border border-[#00E0FF]/30 hover:bg-[#00E0FF]/25 disabled:opacity-50 transition-colors"
                                                                    data-testid={`quick-playback-${r.id}-${p}`}
                                                                >
                                                                    ${p}
                                                                </button>
                                                            );
                                                        })}
                                                        <button
                                                            type="button"
                                                            onClick={() => fireQuickPlayBack(r, null)}
                                                            disabled={quickBusyKey === `${r.id}-all`}
                                                            className="px-2.5 py-1 rounded-full text-[11px] font-black bg-[#00E0FF]/25 text-white border border-[#00E0FF] hover:bg-[#00E0FF]/40 disabled:opacity-50 transition-colors"
                                                            data-testid={`quick-playback-${r.id}-all`}
                                                        >
                                                            All ${remaining.toFixed(2)}
                                                        </button>
                                                    </div>
                                                );
                                            })()}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(rowLimit, rowTotal)} of {rowTotal}
                    </p>
                    <ShowMoreButton testid="pending-payouts-show-more" />
                </div>
            )}

            <PlayItBackDialog
                open={!!dialogRow}
                onOpenChange={closeDialog}
                maxAmount={dialogRow ? Number(dialogRow.unpaid_gross ?? Math.abs(dialogRow.amount || 0)) : 0}
                playerName={dialogRow?.user_name}
                isAdmin={true}
                busy={dialogBusy}
                onConfirm={confirmPlayBack}
            />
            <PartialPaidDialog
                row={payingRow ? (rows.find((x) => x.id === payingRow.id) || payingRow) : null}
                onClose={() => setPayingRow(null)}
                onChanged={async () => { await fetchPending(); onChanged?.(); }}
            />
            {/* Full-screen QR preview overlay — click to dismiss. Renders on
                top of everything so a cashier on their phone can point their
                camera at the screen and scan it. */}
            {qrPreview && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
                    onClick={() => setQrPreview(null)}
                    data-testid="pending-payout-qr-overlay"
                >
                    <div className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
                        <img
                            src={qrPreview}
                            alt="Player payment QR — full size"
                            className="max-w-full max-h-[75vh] rounded-xl border-2 border-[#FFD700] shadow-[0_0_60px_rgba(255,215,0,0.4)]"
                        />
                        {/* Action bar — Download saves the QR to the admin's
                            device so cashiers on a second phone / tablet can
                            still scan it after closing the panel. */}
                        <div className="flex gap-2 flex-wrap justify-center">
                            <a
                                href={qrPreview}
                                download={`payout-qr-${Date.now()}.png`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#39FF14] text-black font-black text-sm hover:bg-[#2FCC10] transition-colors"
                                data-testid="pending-payout-qr-download"
                            >
                                <Download className="w-4 h-4" /> Save to device
                            </a>
                            <button
                                type="button"
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                        // Best-effort clipboard copy of the image
                                        // itself. Falls back gracefully if the
                                        // browser doesn't support ClipboardItem.
                                        const blob = await (await fetch(qrPreview)).blob();
                                        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
                                        toast.success('QR image copied — paste into Cash App');
                                    } catch {
                                        toast.error('Copy not supported — use Save to device instead');
                                    }
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 text-white font-bold text-sm hover:bg-white/20 transition-colors border border-white/20"
                                data-testid="pending-payout-qr-copy-image"
                            >
                                <Copy className="w-4 h-4" /> Copy image
                            </button>
                            <button
                                type="button"
                                onClick={() => setQrPreview(null)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-black text-white font-bold text-sm hover:bg-zinc-900 transition-colors border border-white/20"
                                data-testid="pending-payout-qr-close"
                            >
                                Close
                            </button>
                        </div>
                        <p className="text-white/50 text-xs">Or tap outside to close</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PendingPayoutsPanel;
